import { useState, useEffect } from 'react'
import { apiFetch } from '@/lib/api'

import AsyncSelect from 'react-select/async';
import { FaAngleLeft, FaAngleRight, FaPlay } from 'react-icons/fa';
import { MdCheckBoxOutlineBlank, MdCheckBox, MdOutlineCheckBox   } from "react-icons/md";
import Button from './ui/Button'
import Loader from './loader';
import VideoPreviewModal from './modals/VideoPreviewModal';
import { useModal } from '@/contexts/ModalContext';

export default function WscBrowser({onSelect}) {
    const [systems, setSystems] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [selectedSystem, setSelectedSystem] = useState(null)
    const [games, setGames] = useState([])
    const [teams, setTeams] = useState([])
    const [selectedGames, setSelectedGames] = useState([])
    const [selectedTeams, setSelectedTeams] = useState([])
    const [selectedPlayers, setSelectedPlayers] = useState([])
    const [selectedAssets, setSelectedAssets] = useState([])
    const [assets, setAssets] = useState([])
    const [teamsCache, setTeamsCache] = useState(0)
    const [gamesCache, setGamesCache] = useState(1)
    const [playersCache, setPlayersCache] = useState(2)
    const [videoUrl, setVideoUrl] = useState(null)
    const [totalPages, setTotalPages] = useState(0)
    const [currentPage, setCurrentPage] = useState(0)
    const [pageSize, setPageSize] = useState(50)
    const [assetsCount, setAssetsCount] = useState(0)
    const { openModal } = useModal()
    const searchObj = {
        isMiniLibrary:true,
        hideUnlinked:false,
        eventIdsForSubItems:[],
        andInsideFilters:{},
        orInsideFilters:{
            searchText:"",
            startTime:-1,
            endTime:-1,
            aspectRatios:[],
            gameVideoAttribIDs:[],
            actionIDs:[],
            gameIDs:[],
            tourIDs:[],
            playerIDs:[],
            teamIDs:[],
            seasonIDs:[],
            parameterSetValues:[],
            venueIds:[],
            downloadIndicator:null,
            minRating:2,
            excludedActionIDs:[]
        },
        gameProgress:null,
        gamePartIds:[],
        assetTypes:[8],
        pageRequest:{
            maxResults:1000,
            pageSize:50,
            pageNum:0
        },
        includeChildItems:false,
        excludeMarkersAndTranscriptions:true
    }

    const getGames = async (searchText) => {
        
        try{
            const q = new URLSearchParams({
                searchText: searchText || '', 
                teamIDs: selectedTeams.map(team => team.value).join(','),
                playerIDs: selectedPlayers.map(player => player.value).join(',')
            })
            const gamesResponse = await apiFetch(`/api/wsc/systems/${selectedSystem.system_id}/games?${q.toString()}`, {
                method: 'GET'
            })

            if(!gamesResponse.ok){
                throw new Error('Failed to fetch games')
            }

            const gamesData = await gamesResponse.json()

            return gamesData.result.map(game => ({
                value: game.id,
                label: game.contentDescription
            }))
        } catch (error) {
            setError(error)
        } finally {
            setError(null)
        }
    }

    const getTeams = async (searchText) => {
        try{
            const q = new URLSearchParams({
                searchText: searchText || '', 
                gameIDs: selectedGames.map(game => game.value).join(','),
                playerIDs: selectedPlayers.map(player => player.value).join(',')
            })
            const gamesResponse = await apiFetch(`/api/wsc/systems/${selectedSystem.system_id}/teams?${q.toString()}`, {
                method: 'GET'
            })

            if(!gamesResponse.ok){
                throw new Error('Failed to fetch games')
            }

            const teamsData = await gamesResponse.json()

            return teamsData.result.map(team => ({
                value: team.id,
                label: team.name
            }))
        } catch (error) {
            setError(error)
        } finally {
            setError(null)
        }
    }

    const getPlayers = async (searchText) => {
        try{
            const q = new URLSearchParams({
                searchText: searchText || '', 
                gameIDs: selectedGames.map(game => game.value).join(','),
                teamIDs: selectedTeams.map(team => team.value).join(',')
            })

            const playersResponse = await apiFetch(`/api/wsc/systems/${selectedSystem.system_id}/players?${q.toString()}`, {
                method: 'GET'
            })

            if(!playersResponse.ok){
                throw new Error('Failed to fetch players')
            }

            const playersData = await playersResponse.json()

            return playersData.result.map(player => ({
                value: player.id,
                label: player.name
            }))
        } catch (error) {
            setError(error)
        } finally {
            setError(null)
        }
    }

    const searchAssets = async (pageNum = 0) => {
        setLoading(true)
        try{
            const assetsResponse = await apiFetch(`/api/wsc/systems/${selectedSystem.system_id}/assets`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...searchObj,
                    orInsideFilters:{
                        ...searchObj.orInsideFilters,
                        gameIDs: selectedGames.map(game => game.value),
                        teamIDs: selectedTeams.map(team => team.value),
                        playerIDs: selectedPlayers.map(player => player.value)
                    },
                    pageRequest:{
                        ...searchObj.pageRequest,
                        pageNum:pageNum
                    }
                })
            })

            if(!assetsResponse.ok){
                throw new Error('Failed to fetch assets')
            }

            const assetsData = await assetsResponse.json()
            setAssets(assetsData.items)
            setTotalPages(assetsData.pageResult.totalPages)
            setPageSize(assetsData.pageResult.pageSize)
            setAssetsCount(assetsData.pageResult.totalCount)
            setCurrentPage(assetsData.pageResult.pageNum)
        } catch (error) {
            setError(error)
        } finally {
            setLoading(false)
            setError(null)
        }
    }

    const fetchData = async () => {
        try {
            const response = await apiFetch('/api/wsc/systems')
            const data = await response.json()
            setSystems(data)
            setSelectedSystem(data[0] || null)
        } catch (error) {
            setError(error)
        } finally {
            setLoading(false)
        }
    }

    const handleGameChange = (e) => {
        setSelectedGames(e)
    }

    const handleTeamChange = (e) => {
        setSelectedTeams(e)
    }

    const handlePlayerChange = (e) => {
        setSelectedPlayers(e)
    }

    useEffect(() => {
        setTeamsCache(Math.random())
        setPlayersCache(Math.random())
        if (selectedSystem)  searchAssets()
    }, [selectedGames])

    useEffect(() => {
        setGamesCache(Math.random())
        setPlayersCache(Math.random())
        if (selectedSystem)  searchAssets()
    }, [selectedTeams])

    useEffect(() => {
        setGamesCache(Math.random())
        setTeamsCache(Math.random())
        if (selectedSystem)  searchAssets()
    }, [selectedPlayers])

    useEffect(() => {
        if (selectedSystem)  searchAssets()
    }, [selectedSystem])

    useEffect(() => {
        fetchData()
    }, [])

    const handlePreviousPage = () => {
        if (currentPage > 0) {
            searchAssets(currentPage - 1)
        }
    }

    const handleNextPage = () => {
        if (currentPage < totalPages - 1) {
            searchAssets(currentPage + 1)
        }
    }

    const handleImport = () => {
        if(onSelect){
            onSelect(selectedAssets || [])
        }
    }

    const handleCancel = () => {
        if(onSelect){
            onSelect([])
        }
    }

    const handleAssetsPreview = (asset) => {
        openModal(<VideoPreviewModal video={{url: asset.videoUrl}} />)
    }

    const multiSelectClassNames = {
        control: (state) =>  'bg-gray-900 border border-gray-800 rounded px-2 py-1 text-xs w-full min-h-[30px]',
        // input: (state) => state.isFocused ? 'text-blue-500' : 'text-gray-800',
        menu: (state) =>  'border-gray-900',
        menuList: (state) =>  'bg-gray-900 text-gray-200',
        option: (state) =>  'p-2 text-[0.75rem !important] hover:bg-gray-800',
        multiValue: (state) => state.isFocused ? 'bg-blue-500 text-white' : 'bg-gray-800 text-gray-200',
        placeholder: (state) =>  'text-gray-500',
    }

    return (
        <div className='w-full h-full flex flex-col'>
            <div className='flex items-center justify-between px-10 h-20'>
                <div className='flex items-center gap-2'>
                    <h1 className='text-2xl font-bold border-l-4 border-primary pl-4'>WSC Browser</h1>
                </div>
            </div>
            <div className='grid grid-cols-5 gap-2 px-10 py-2 border-b border-gray-900'>
                <select className="bg-gray-900 border border-gray-800 rounded px-2 py-1 text-sm" onChange={(e)=> setSelectedSystem(systems.find(s => s.system_id?.toString() === e.target.value))}>
                    {systems.map(system => (
                        <option key={system.system_id} value={system.system_id}>
                            {system.name}
                        </option>
                    ))}
                </select>
                {selectedSystem && <>
                    <AsyncSelect
                        key={gamesCache}
                        isMulti
                        name="games"
                        loadOptions={getGames}
                        value={selectedGames}
                        defaultOptions
                        unstyled={true}
                        classNames={multiSelectClassNames}
                        placeholder="Games"
                        onChange={handleGameChange}
                    />

                    <AsyncSelect
                        key={teamsCache}
                        isMulti
                        name="teams"
                        defaultOptions
                        value={selectedTeams}
                        loadOptions={getTeams}
                        unstyled={true}
                        classNames={multiSelectClassNames}
                        placeholder="Teams"
                        onChange={handleTeamChange}
                    />

                    <AsyncSelect
                        key={playersCache}
                        isMulti
                        name="players"
                        defaultOptions
                        value={selectedPlayers}
                        loadOptions={getPlayers}
                        unstyled={true}
                        classNames={multiSelectClassNames}
                        placeholder="Players"
                        onChange={handlePlayerChange}
                    />
                </>}
            </div>
            <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 px-10 py-4 overflow-y-auto flex-1'>
                {assets.map(asset => (
                    <div key={asset.uid} className='flex flex-col gap-2 cursor-pointer group'>
                        <div className='flex relative w-full aspect-video rounded-md overflow-hidden'>
                            <img src={asset.thumbnailUrl2} alt={asset.eventName} loading="lazy" decoding="async" style={{position:'absolute',width:'100%',height:'100%',objectFit:'cover'}} className='object-cover' />
                            <div className='absolute top-0 left-0 w-full h-full bg-black/50 items-center justify-center hidden group-hover:flex' onClick={() => handleAssetsPreview(asset)}>
                                <FaPlay className='w-5 h-5 text-white' />
                            </div>
                            <div className='absolute top-0 left-0 p-2 bg-black/80 items-center justify-center flex'>
                                {selectedAssets.some(a => a.uid === asset.uid) ? 
                                    <MdCheckBox className='w-4 h-4 text-white' onClick={() => setSelectedAssets(selectedAssets.filter(a => a.uid !== asset.uid))} /> 
                                    : 
                                    <MdCheckBoxOutlineBlank className='w-4 h-4 text-white' onClick={() => setSelectedAssets([...selectedAssets, asset])} />
                                }
                            </div>
                        </div>
                        <span className='text-xs text-secondary-foreground'>{asset.eventName}</span>
                    </div>
                ))}
            </div>
            <div className='flex items-center justify-between border-t border-gray-800 gap-2 p-2 text-xs text-gray-200'>
                <div className='flex items-center gap-2'>
                    <button className='flex rounded-md bg-gray-950 text-xs items-center justify-center border-gray-900 px-2 py-1 cursor-pointer hover:bg-gray-800' onClick={handlePreviousPage}>
                        <FaAngleLeft className='w-4 h-4' />
                    </button>
                    <span>{currentPage+1}/{totalPages}</span>
                    <button className='flex rounded-md bg-gray-950 text-xs items-center justify-center border-gray-900 px-2 py-1 cursor-pointer hover:bg-gray-800' onClick={handleNextPage}>
                        <FaAngleRight className='w-4 h-4' />
                    </button>
                    <span>{`${(currentPage * pageSize)+1}-${(currentPage * pageSize)+pageSize > assetsCount ? assetsCount : (currentPage * pageSize)+pageSize} of ${assetsCount}`}</span>
                    {selectedAssets.length > 0 && <Button type='secondary' onClick={()=>setSelectedAssets([])}>Clear Selection</Button>}
                </div>
                
                <div className='flex gap-2'>
                    <Button type='danger-ghost' onClick={handleCancel}>Cancel</Button>
                    {selectedAssets.length > 0 && <Button type='primary' onClick={handleImport}>Import {selectedAssets.length} assets</Button> }
                </div>

            </div>
            {loading && <Loader />}
        </div>
    )
}